<template>
  <div id="sma-natcon-payment-create">
    <v-overlay :value="ProgressLoadingFlag">
      <v-progress-circular indeterminate size="64" color="primary"></v-progress-circular>
    </v-overlay>

    <!-- Premium Page Header -->
    <v-card elevation="20" class="header-card mb-6 overflow-hidden">
      <div class="header-gradient">
        <div class="header-backdrop-effect"></div>
        <div class="header-content pa-8">
          <v-row align="center" justify="space-between">
            <v-col cols="12" md="8">
              <div class="d-flex align-center">
                <div class="header-icon-box">
                  <v-icon large color="white">mdi-ticket-account</v-icon>
                </div>
                <div class="ml-6">
                  <h1 class="header-title white--text mb-1">
                    {{ PageTitle }}
                  </h1>
                  <p class="header-subtitle white--text mb-0">
                    {{ PageDescription }}
                  </p>
                </div>
              </div>
            </v-col>
            <v-col cols="12" md="4" class="text-right">
              <div class="d-flex justify-end flex-wrap gap-2">
                <v-btn
                  @click.prevent="refreshPageData"
                  color="rgba(255,255,255,0.2)"
                  elevation="0"
                  rounded
                  large
                  class="action-btn"
                  title="Refresh page data"
                >
                  <v-icon left>mdi-refresh</v-icon>
                  Refresh
                </v-btn>
                <v-btn
                  @click.prevent="resetForm"
                  color="rgba(255,255,255,0.2)"
                  elevation="0"
                  rounded
                  large
                  class="action-btn"
                  title="Clear form"
                >
                  <v-icon left>mdi-eraser</v-icon>
                  Clear
                </v-btn>
              </div>
            </v-col>
          </v-row>
        </div>
      </div>
    </v-card>

      <v-card-text>
        <v-dialog v-model="addNonMemberRecordPrompt" persistent max-width="75%">
          <add-sma-non-member
            pageTitle="Add Participant details"
            pageDescription="Add non member details"
            :showDialog="addNonMemberRecordPrompt"
            @hideDialog="addNewNonMember($event)"
            v-if="addNonMemberRecordPrompt"
          ></add-sma-non-member>
        </v-dialog>

        <!-- Notes Alert -->
        <v-alert
          type="info"
          prominent
          border="left"
          class="notes-alert mb-6"
          v-if="NotesFlag"
          icon="mdi-information-outline"
        >
          <div class="alert-content">
            <h3 class="alert-title">Important Instructions</h3>
            <ol class="alert-list">
              <li>Regular subscription can be paid only once.</li>
              <li>After paying regular subscription, choose additional subscription.</li>
              <li>Late fee is optional but paying late fee is required to get voting rights.</li>
              <li>After completing the payment, kindly select your members for whom the payment was made.</li>
            </ol>
          </div>
        </v-alert>
        
        <!-- Required Fields Note -->
        <v-card class="required-note-card mb-6" elevation="0">
          <div class="required-note-content">
            <div class="required-note-icon">
              <v-icon large color="white">mdi-information</v-icon>
            </div>
            <div class="required-note-text">
              <span class="required-note-label">
                <span class="asterisk">*</span> Indicates Required Field
              </span>
            </div>
          </div>
        </v-card>
        
        <div class="d-flex justify-content-center mb-3" v-if="LoadingFlag">
          <b-spinner
            variant="primary"
            type="grow"
            label="Loading..."
          ></b-spinner>
        </div>
        <v-container v-if="StartupLoadingFlag">
          <v-row wrap>
            <v-col cols="12" md="4" v-for="n in 6" :key="n">
              <v-skeleton-loader
                v-bind="attrs"
                type="paragraph"
              ></v-skeleton-loader>
            </v-col>
          </v-row>
        </v-container>
        <v-container v-if="!StartupLoadingFlag">
          <v-form ref="form2" v-model="valid1" lazy-validation>
            <!-- Registration Details Card -->
            <v-card elevation="8" class="form-card mb-6">
              <div class="form-card-header">
                <v-icon large class="header-icon">mdi-clipboard-list-outline</v-icon>
                <div class="ml-4">
                  <h2 class="form-card-title">Registration Details</h2>
                  <p class="form-card-subtitle">Select registration type and member</p>
                </div>
              </div>
              <v-divider class="my-0"></v-divider>
              <v-card-text class="pt-6">
                <v-row wrap>
                  <v-col cols="12" md="4">
                    <h4 class="field-label">
                      <v-icon small class="mr-2" color="primary">mdi-account-multiple</v-icon>
                      <span class="text-danger">*</span> Member Type
                    </h4>
                    <v-select
                      v-model="MemberType"
                      :items="MemberTypeOptions"
                      :rules="MemberTypeRules"
                      :reduce="(option) => option.value"
                      required
                      outlined
                      dense
                      rounded
                      class="form-control"
                    ></v-select>
                  </v-col>
                  <v-col cols="12" md="4" v-if="MemberType == 2">
                    <h4 class="field-label">
                      <v-icon small class="mr-2" color="primary">mdi-account-search</v-icon>
                      <span class="text-danger">*</span> Select Member
                    </h4>
                    <v-autocomplete
                      @change="getSelectedMemberDetails"
                      :reduce="(option) => option.value"
                      :loading="MemberIdOptionsLoading"
                      :items="MemberIdOptions"
                      :rules="MemberIdRules"
                      v-model="MemberId"
                      required
                      outlined
                      dense
                      rounded
                      clearable
                      class="form-control"
                    >
                      <template v-slot:selection="{ item }">
                        <img width="40" height="40" :src="item.photo" class="rounded-circle mr-2" />{{
                          item.text
                        }}
                      </template>
                      <template v-slot:item="{ item }">
                        <img width="40" height="40" :src="item.photo" class="rounded-circle mr-2" />{{
                          item.text
                        }}
                      </template>
                    </v-autocomplete>
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>

            <!-- Member Details Card -->
            <v-card
              elevation="8"
              class="form-card mb-6"
              v-if="MemberType != '' && MemberId != ''"
            >
              <div class="form-card-header">
                <v-icon large class="header-icon">mdi-account-details</v-icon>
                <div class="ml-4">
                  <h2 class="form-card-title">{{ RegisterDetailsTxt }}</h2>
                  <p class="form-card-subtitle">Verify and update member information</p>
                </div>
              </div>
              <v-divider class="my-0"></v-divider>
              <v-card-text class="pt-6">
                <v-row wrap>
                  <v-col cols="12" sm="4" md="3" class="text-center">
                    <div class="member-photo-container">
                      <v-img
                        :src="selectedMember.MemberImagePath"
                        :alt="selectedMember.MemberName"
                        lazy-src="/no-image-available.png"
                        max-width="140"
                        max-height="140"
                        class="member-photo"
                        v-if="selectedMember.MemberImagePath != ''"
                      >
                        <template v-slot:placeholder>
                          <v-row
                            class="fill-height ma-0"
                            align="center"
                            justify="center"
                          >
                            <v-progress-circular
                              indeterminate
                              color="primary"
                            ></v-progress-circular>
                          </v-row>
                        </template>
                      </v-img>
                      <div v-else class="placeholder-photo">
                        <v-icon large color="primary">mdi-account-circle</v-icon>
                      </div>
                    </div>
                  </v-col>
                  <v-col cols="12" sm="8" lg="3" md="3">
                    <div class="info-card">
                      <h4 class="field-label">
                        <v-icon small class="mr-2" color="primary">mdi-card-account-details</v-icon>
                        Member Details
                      </h4>
                      <p class="info-value">{{ selectedMember.MemberName }}</p>
                      <p class="info-label">Name</p>
                      <p class="info-value">{{ selectedMember.MembershipId }}</p>
                      <p class="info-label">Membership ID</p>
                    </div>
                  </v-col>
                  <v-col cols="12" sm="6" lg="3" md="3">
                    <h4 class="field-label">
                      <v-icon small class="mr-2" color="primary">mdi-email</v-icon>
                      <span class="text-danger">*</span> Email
                    </h4>
                    <v-text-field
                      v-model="selectedMember.EmailId"
                      :rules="EmailIdRules"
                      hint="Enter the Email"
                      outlined
                      dense
                      rounded
                      class="form-control"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" sm="6" lg="3" md="3">
                    <h4 class="field-label">
                      <v-icon small class="mr-2" color="primary">mdi-phone</v-icon>
                      <span class="text-danger">*</span> Phone No
                    </h4>
                    <v-text-field
                      v-model="selectedMember.MobileNo"
                      :rules="MobileNoRules"
                      :counter="10"
                      v-mask="'##########'"
                      hint="Enter the Phone No"
                      outlined
                      dense
                      rounded
                      class="form-control"
                    ></v-text-field>
                  </v-col>
                  <v-col
                    cols="12"
                    md="3"
                    v-if="selectedMember.MemberId > 0 && MemberType != 3"
                  >
                    <h4 class="field-label">
                      <v-icon small class="mr-2" color="primary">mdi-gender-male-female</v-icon>
                      <span class="text-danger">*</span> Gender
                    </h4>
                    <v-select
                      :reduce="(option) => option.value"
                      v-model="selectedMember.Gender"
                      :items="GenderOptions"
                      :rules="GenderRules"
                      required
                      outlined
                      dense
                      rounded
                      class="form-control"
                    ></v-select>
                  </v-col>
                  <v-col
                    cols="12"
                    md="3"
                    v-if="selectedMember.MemberId > 0 && MemberType != 3"
                  >
                    <h4 class="field-label">
                      <v-icon small class="mr-2" color="primary">mdi-food</v-icon>
                      <span class="text-danger">*</span> Food Preference
                    </h4>
                    <v-select
                      :reduce="(option) => option.value"
                      v-model="selectedMember.FoodChoice"
                      :items="FoodPreferenceOptions"
                      :rules="FoodPreferenceRules"
                      required
                      outlined
                      dense
                      rounded
                      class="form-control"
                    ></v-select>
                  </v-col>
                  <v-col cols="12" md="3" v-if="selectedMember.MemberId > 0">
                    <h4 class="field-label">
                      <v-icon small class="mr-2" color="primary">mdi-file-document</v-icon>
                      Do you want GST Bill <small>(optional)</small>
                    </h4>
                    <v-select
                      :reduce="(option) => option.value"
                      v-model="selectedMember.GstNumberFlag"
                      :items="GstNumberFlagOptions"
                      :rules="GstNumberFlagRules"
                      required
                      outlined
                      dense
                      rounded
                      class="form-control"
                    ></v-select>
                  </v-col>
                  <v-col
                    cols="12"
                    sm="6"
                    md="3"
                    v-if="
                      selectedMember.MemberId > 0 &&
                      selectedMember.GstNumberFlag == 1
                    "
                  >
                    <h4 class="field-label">
                      <v-icon small class="mr-2" color="primary">mdi-identifier</v-icon>
                      <span class="text-danger">*</span> GST Number
                    </h4>
                    <v-text-field
                      v-model="selectedMember.GstNumber"
                      :rules="GstNumberRules"
                      :counter="15"
                      v-mask="'##AAAAA####A#ZN'"
                      hint="Enter your GST Number"
                      dense
                      outlined
                      rounded
                      class="form-control"
                    ></v-text-field>
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>

            <!-- Participants Card -->
            <v-card elevation="8" class="form-card mb-6" v-if="MemberType == 3">
              <div class="form-card-header">
                <v-icon large class="header-icon">mdi-account-group</v-icon>
                <div class="ml-4">
                  <h2 class="form-card-title">Participant Details</h2>
                  <p class="form-card-subtitle">Manage participant information</p>
                </div>
              </div>
              <v-divider class="my-0"></v-divider>
              <v-card-text class="pt-6">
                <v-row wrap>
                  <v-col cols="12" sm="12" lg="12" md="12" id="step9">
                    <v-data-table
                      :headers="tableColumns2"
                      :items="AllParticipants"
                      hide-default-footer
                      disable-sort
                      id="document-table"
                      class="professional-table"
                    >
                      <template v-slot:no-data>
                        <div class="text-center py-8">
                          <v-icon large color="primary" class="mb-3">mdi-account-multiple-plus</v-icon>
                          <p class="text-body1">No participants added yet</p>
                          <v-btn
                            icon
                            color="primary"
                            @click.prevent="addNonMemberRecordPrompt = true"
                            class="mt-2"
                          >
                            <v-icon>mdi-plus-circle</v-icon>
                          </v-btn>
                        </div>
                      </template>
                      <template v-slot:item="{ item, index }">
                        <tr class="table-row">
                          <td class="table-cell">
                            <v-chip
                              small
                              :color="item.Gender === 'Male' ? '#667eea' : '#764ba2'"
                              text-color="white"
                              class="mr-2"
                            >
                              <v-icon left small>{{ item.Gender === 'Male' ? 'mdi-gender-male' : 'mdi-gender-female' }}</v-icon>
                              {{ item.ParticipantName }}
                            </v-chip>
                          </td>
                          <td class="table-cell">
                            <v-chip small text-color="white" :color="getGenderColor(item.Gender)">
                              {{ item.Gender }}
                            </v-chip>
                          </td>
                          <td class="table-cell">{{ item.Age }}</td>
                          <td class="table-cell">
                            <v-chip small outlined color="primary">
                              <v-icon left x-small>mdi-phone</v-icon>
                              {{ item.MobileNo }}
                            </v-chip>
                          </td>
                          <td class="table-cell">
                            <v-chip small outlined color="info">
                              <v-icon left x-small>mdi-email</v-icon>
                              {{ item.EmailId }}
                            </v-chip>
                          </td>
                          <td class="table-cell">
                            <v-chip
                              small
                              :color="item.Food === 'Veg' ? '#10b981' : '#f59e0b'"
                              text-color="white"
                            >
                              <v-icon left x-small>{{ item.Food === 'Veg' ? 'mdi-leaf' : 'mdi-grilling' }}</v-icon>
                              {{ item.Food }}
                            </v-chip>
                          </td>
                          <td class="table-cell">
                            <v-btn
                              icon
                              small
                              color="primary"
                              @click.prevent="openNonMemberDialog"
                              title="Add Participant"
                            >
                              <v-icon small>mdi-plus-circle</v-icon>
                            </v-btn>
                            <v-btn
                              icon
                              small
                              color="error"
                              @click="deleteRow(1, index, item)"
                              title="Delete Participant"
                            >
                              <v-icon small>mdi-delete</v-icon>
                            </v-btn>
                          </td>
                        </tr>
                      </template>
                    </v-data-table>
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>

            <!-- Total Participants Card -->
            <v-card elevation="8" class="form-card mb-6">
              <div class="form-card-header">
                <v-icon large class="header-icon">mdi-counter</v-icon>
                <div class="ml-4">
                  <h2 class="form-card-title">Total Participants</h2>
                  <p class="form-card-subtitle">Number of participants for this registration</p>
                </div>
              </div>
              <v-divider class="my-0"></v-divider>
              <v-card-text class="pt-6">
                <v-row justify="center">
                  <v-col cols="12" md="4" class="text-center">
                    <h1 class="quantity-display">{{ Qty }}</h1>
                    <v-text-field
                      v-model="Qty"
                      outlined
                      dense
                      type="number"
                      v-if="QtyFlag"
                      :rules="QtyRules"
                      class="form-control"
                    ></v-text-field>
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>

            <v-dialog v-model="PreviewPlanFlag" width="500">
              <event-plan-details
                :Plan="PreviewPlanDetails"
                @hideDialog="hidePreviewPlanPopup($event)"
                v-if="PreviewPlanFlag"
              ></event-plan-details>
            </v-dialog>

            <!-- Plans Section -->
            <h2 class="plans-section-title mb-6">
              <v-icon color="primary" class="mr-3">mdi-tag-multiple</v-icon>
              Available Plans
            </h2>
            <v-row justify="center" wrap class="plans-container">
              <div v-for="(item, index) in Plans" :key="index">
                <v-col cols="12" sm="6" md="3" lg="3">
                  <div v-if="item.Recommended">
                    <div class="cards-box" v-if="item.PlanVisibleFlag">
                      <div class="cards popular">
                        <div class="cards-ribbon">
                          <span>most popular</span>
                        </div>
                        <div class="cards-title-bar">
                          <h3 class="cards-title">{{ item.PlanName }}</h3>
                          <h4
                            class="cards-subtitle"
                            v-if="item.PlanDescription != ''"
                          >
                            {{ item.PlanDescription }}
                          </h4>
                        </div>
                        <div class="cards-price">
                          <h4 class="cards-price-amount">
                            {{ item.SubTotalTxt }}
                            <br />
                            <span class="cards-price-gst"
                              >+ {{ item.TaxAmountTxt }} gst</span
                            >
                            <small class="cards-price-validity">per head</small>
                          </h4>
                          <v-divider />
                          <v-btn
                            color="primary"
                            dark
                            @click.prevent="showPreviewPlanPopup(item)"
                            >Show Plan details</v-btn
                          >
                        </div>
                        <br />
                        <div class="cards-price">
                          <v-divider />
                          <h1 class="cards-price-amount">
                            <small class="cards-price-validity"
                              >Toatal Amount</small
                            >
                            ₹ {{ item.TotalAmount }}
                          </h1>
                        </div>
                        <br /><br />
                        <div class="cards-action">
                          <button
                            class="cards-action-button"
                            :disabled="!valid1"
                            @click.prevent="confirmAlert(item)"
                          >
                            Pay now
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div v-else>
                    <div class="cards-box" v-if="item.PlanVisibleFlag">
                      <div class="cards">
                        <div class="cards-title-bar">
                          <h3 class="cards-title">{{ item.PlanName }}</h3>
                          <h4
                            class="cards-subtitle"
                            v-if="item.PlanDescription != ''"
                          >
                            {{ item.PlanDescription }}
                          </h4>
                        </div>
                        <div class="cards-price">
                          <h4 class="cards-price-amount">
                            {{ item.SubTotalTxt }}
                            <br />
                            <span class="cards-price-gst"
                              >+ {{ item.TaxAmountTxt }} gst</span
                            >
                            <small class="cards-price-validity">per head</small>
                          </h4>
                          <v-divider />
                          <v-btn
                            color="primary"
                            dark
                            @click.prevent="showPreviewPlanPopup(item)"
                            >Show Plan details</v-btn
                          >
                        </div>
                        <br />
                        <div class="cards-price">
                          <v-divider />
                          <h1 class="cards-price-amount">
                            <small class="cards-price-validity"
                              >Toatal Amount</small
                            >
                            ₹ {{ item.TotalAmount }}
                          </h1>
                        </div>
                        <br /><br />
                        <div class="cards-action">
                          <button
                            class="cards-action-button"
                            :disabled="!valid1"
                            @click.prevent="confirmAlert(item)"
                            :loading="SubmitFlag"
                          >
                            Pay now
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </v-col>
              </div>
            </v-row>
          </v-form>
        </v-container>
        <br /><br />
      </v-card-text>
  </div>
</template>

<script>
import common from "@/view/Common.vue";
import companyConfig from "@/company_config.json";
import Swal from "sweetalert2";
import { SET_BREADCRUMB } from "@/core/services/store/breadcrumbs.module";
import AddSmaNonMember from "@/view/pages/erp/events/sma-natcon/AddSmaNonMember.vue";
import EventPlanDetails from "@/view/pages/erp/events/natcon/EventPlanDetails.vue";

export default {
  mixins: [common],
  components: {
    AddSmaNonMember,
    EventPlanDetails,
  },
  data() {
    return {
      valid1: true,
      valid2: true,
      LoadingFlag: false,
      StartupLoadingFlag: false,
      ProgressLoadingFlag: false,
      SubmitFlag: false,
      SearchFlag: false,
      NotesFlag: false,
      rows: [],
      tableColumns1: [],

      PreviewPlanFlag: false,
      PreviewPlanDetails: {},

      selected: [],
      search: "",

      MemberPaymentLink: "",
      RegisterDetailsTxt: "Member Details",

      RegistrationTypeId: "",

      Qty: "",
      QtyFlag: false,
      QtyRules: [
        (v) => Number.isInteger(Number(v)) || "Nos is invalid",
        (v) => v > 0 || "Number must be greater than zero",
        (v) => v < 7 || "Number must be less than 6",
      ],

      MemberType: "",
      MemberTypeRules: [(v) => !!v || "Member Type for is required"],
      MemberTypeOptions: [],

      Gender: "",
      GenderRules: [(v) => !!v || "Gender is required"],
      GenderOptions: [
        { value: "", text: "--Select--" },
        { value: 1, text: "Male" },
        { value: 2, text: "Female" },
        { value: 3, text: "Other" },
      ],

      FoodPreference: "",
      FoodPreferenceRules: [(v) => !!v || "Food preference is required"],
      FoodPreferenceOptions: [
        { value: "", text: "--Select--" },
        { value: 1, text: "Veg" },
        { value: 2, text: "Non Veg" },
      ],

      Lom: {},

      Member: {},

      Presidentmember: {},
      Plans: [],

      selectedPlan: {},
      selectedMember: {},

      MobileNoRules: [(v) => !!v || "Mobile No is required"],

      EmailIdRules: [
        (v) => !!v || "E-mail is required",
        (v) => /.+@.+\..+/.test(v) || "E-mail must be valid",
      ],

      Events: {},

      MemberIdRules: [(v) => !!v || "Member is required"],
      MemberId: "",
      MemberIdOptions: [],
      MemberIdOptionsLoading: false,

      CheckoutTableHeaders: [],

      tab: null,
      TabDisable1: false,
      TabDisable2: false,

      TotalAmount: 0,
      Contribution: [],

      TotalAmount1: 0,
      TotalAmount2: 0,

      Member: {},
      Order: {},
      MemberImageFlag: false,

      PenaltySubscription: [],

      direction: "top",
      fab: true,
      fling: false,
      hover: true,
      tabs: null,
      top: false,
      right: true,
      bottom: true,
      left: false,
      transition: "slide-y-reverse-transition",

      OrderType: 1,

      JciYearCodeRules: [(v) => !!v || "Year is required"],
      JciYearCodeOptions: [],
      JciYearCodeOptionsLoading: false,

      MobileNoRules: [(v) => !!v || "Mobile No is required"],

      EmailIdRules: [
        (v) => !!v || "E-mail is required",
        (v) => /.+@.+\..+/.test(v) || "E-mail must be valid",
      ],

      GstNumberFlag: 2,
      GstNumberFlagRules: [],
      GstNumberFlagOptions: [
        { value: "", text: "--Select--" },
        { value: 1, text: "Yes" },
        { value: 2, text: "No" },
      ],

      GstNumber: "",
      GstNumberRules: [(v) => !!v || "GST number is required"],

      ZoneCode: "",
      LomCode: "",
      MemberId: "",
      UserMemberId: "",

      EventId: "",
      EventTypeId: 11,

      addNonMemberRecordPrompt: false,
      ParticipantMaxLimit: 6,
      AllParticipants: [],
      NonMemberDetails: {},
      uploadedParticipantImages: [],
      tableColumns2: [
        { text: "Participant Name", value: "ParticipantName" },
        { text: "Gender", value: "Gender" },
        { text: "Age", value: "Age" },
        { text: "Mobile number", value: "MobileNo" },
        { text: "Email Id", value: "EmailId" },
        { text: "Food", value: "Food" },
        { text: "Action", value: "action" },
      ],

      Order: {
        back_url: "",
        customer_name: "",
        customer_email_id: "",
        customer_mobile_no: "",
        rzp_action: "#",
        rzp_account_id: "",
        rzp_key: "bdgzbd",
        rzp_amount: "",
        rzp_order_id: "",
        rzp_buttontext: "Pay now",
        rzp_name: "JCI India Foundation",
        rzp_description: "",
        rzp_image: "",
        rzp_color: "#0096d5",
      },

      PageInfo: {},
      PageTitle: "",
      PageDescription: "",
      CurrentYearId: 0,
      JciYearCode: "",
      YearName: "",
      NextYearCode: "",
      NextYearName: "",
      RoleId: "",
    };
  },
  computed: {},
  mounted() {
    /*
    this.$store.dispatch(SET_BREADCRUMB, [
      { title: "Vuetify", route: "alerts" },
      { title: "Form Inputs & Control", route: "autocompletes" },
      { title: "Fileinptus" },
    ]);
    */
  },
  watch: {
    PageInfo: function () {
      console.log("watch PageInfo");
      this.LoadingFlag = false;
      var tr = this.PageInfo;
      console.log({ tr });
      var n1 = Object.keys(tr).length;
      console.log({ n1 });
      if (n1 > 0) {
        this.PageTitle = tr.PageTitle;
        this.PageDescription = tr.PageDescription;

        var CurrentJciYearId = parseInt(tr.CurrentJciYearId);
        console.log({ CurrentJciYearId });
        this.JciYearCode = CurrentJciYearId;

        var YearName = parseInt(tr.CurrentJciYearName);
        console.log({ YearName });
        this.YearName = YearName;

        var NextJciYearId = parseInt(tr.NextJciYearId);
        console.log({ NextJciYearId });
        this.NextYearCode = NextJciYearId;

        var NextJciYearName = parseInt(tr.NextJciYearName);
        console.log({ NextJciYearName });
        this.NextYearName = NextJciYearName;

        this.CurrentYearId = CurrentJciYearId;

        var UserMemberId = this.$session.get("MemberId");
        this.UserMemberId = UserMemberId;
        console.log({ UserMemberId });

        var ZoneId = this.$session.get("ZoneId");
        this.ZoneCode = ZoneId;
        console.log({ ZoneId });

        var LomId = this.$session.get("LomId");
        this.LomCode = LomId;
        console.log({ LomId });

        var RoleId = this.$session.get("RoleId");
        RoleId = RoleId == (null || undefined) ? "" : RoleId;
        console.log({ RoleId });
        this.RoleId = RoleId;
        var MemberPaymentLink =
          RoleId == 9
            ? "/natcon/payment/create"
            : "/natcon/member-payment/create";
        console.log({ MemberPaymentLink });
        this.MemberPaymentLink = MemberPaymentLink;

        if (RoleId == 9) {
          var MemberTypeOptions = [
            { value: "", text: "--Select Type--" },
            { value: 2, text: "For JAC member" },
            { value: 3, text: "For Relatives" },
          ];
        } else {
          var MemberTypeOptions = [
            { value: "", text: "--Select Type--" },
            { value: 1, text: "For me" },
            { value: 2, text: "For another JAC member" },
            { value: 3, text: "For Relatives" },
          ];
        }
        console.log({ MemberTypeOptions });
        this.MemberTypeOptions = MemberTypeOptions;

        this.pageData();
      }
    },
    JciYearCodeOptions: function () {
      console.log("watch JciYearCodeOptions");
      this.LoadingFlag = false;
      this.JciYearCodeOptionsLoading = false;
    },
    Qty: function () {
      console.log("watch Qty");
      var Qty = this.Qty;
      Qty = Qty == (null || undefined || isNaN(Qty)) ? 0 : parseFloat(Qty);
      console.log({ Qty });
      var Plans = this.Plans;
      console.log({ Plans });
      var newPlans = [];
      Plans.forEach((item) => {
        console.log({ item });
        var temp = item;
        var Amount = parseFloat(item.SubTotalAmount);
        console.log({ Amount });
        var TotalAmount = Qty * Amount;
        TotalAmount =
          TotalAmount == (null || undefined || isNaN(TotalAmount))
            ? 0
            : parseFloat(TotalAmount);
        console.log({ TotalAmount });
        temp.Qty = Qty;
        temp.TotalAmount = TotalAmount;
        newPlans.push(temp);
      });
      this.Plans = newPlans;
    },
    MemberIdOptions: function () {
      this.MemberIdOptionsLoading = false;
    },
    MemberType: function () {
      console.log("watch MemberType");
      this.selectedMember = {};
      var MemberType = this.MemberType;
      console.log("MemberType=" + MemberType);
      if (MemberType != "") {
        this.AllParticipants = [];
        this.Qty = 0;
        var Member = this.Member;
        console.log({ Member });
        var MemberId = Member.MemberId;
        console.log({ MemberId });
        var tempData = {
          MemberFlag: true,
          MemberId: MemberId,
          MembershipId: Member.MembershipId,
          MemberName: Member.Fullname,
          Gender: "",
          Age: "",
          MobileNo: Member.MobileNo,
          EmailId: Member.EmailId,
          Gender: Member.Gender,
          FoodChoice: Member.FoodChoice,
          MemberImagePath: Member.MemberImage,
          action: "",
        };
        console.log({ tempData });
        if (MemberType == 1) {
          this.RegisterDetailsTxt = "Member Details";
          // this.AllParticipants.push(tempData);
          this.selectedMember = tempData;
          this.Qty = 1;
          this.MemberId = MemberId;
        } else if (MemberType == 2) {
          this.MemberId = "";
          this.RegisterDetailsTxt = "Member Details";
          this.getSelectedMemberDetails();
        } else {
          this.RegisterDetailsTxt = "Payee Details";
          this.selectedMember = tempData;
          this.MemberId = MemberId;
        }
      }
    },
  },
  methods: {
    refreshPageData() {
      console.log("refreshPageData called");
      this.LoadingFlag = true;
      var id = this.$route.query.id;
      console.log({ id });
      this.EventId = id;
      var url1 = "api/jci-program/show";
      var condition1 = {
        UserInterface: 1,
        Program: id,
      };
      this.getPageDetails("PageInfo", {}, url1, condition1);
    },
    pageData() {
      console.log("pageData called");
      this.getMemberDetails();
      this.getMemberIdOptions();
      this.getEventsDetails();
    },
    // code 1
    validate() {
      if (this.$refs.form2.validate()) {
        this.snackbar = true;
      }
    },
    addRecord() {
      console.log("addRecord called");
      var url = "/staff/list";
      var params = {};
      this.pageRedirect(params, url);
    },
    resetForm() {
      this.$refs.form2.reset();
      this.selectedMember = {};
      this.selectedPlan = {};
      // this.refreshPageData();
    },
    resetValidation() {
      this.$refs.form2.resetValidation();
    },
    openNonMemberDialog() {
      console.log("openNonMemberDialog called");
      var n2 = this.AllParticipants.length;
      console.log({ n2 });
      var m2 = this.ParticipantMaxLimit;
      console.log({ m2 });
      if (n2 < m2) {
        this.addNonMemberRecordPrompt = true;
      } else {
        var message = "Participant maximum limit " + m2 + " reached. ";
        this.sweetAlert("error", message, false);
      }
    },
    showPreviewPlanPopup(tr) {
      this.PreviewPlanDetails = tr;
      this.PreviewPlanFlag = true;
    },
    hidePreviewPlanPopup() {
      this.PreviewPlanDetails = {};
      this.PreviewPlanFlag = false;
    },
    getGenderColor(gender) {
      if (gender === "Male") return "#667eea";
      if (gender === "Female") return "#764ba2";
      return "#f59e0b";
    },
    setQty() {
      console.log("setQty called");
      var n3 = this.AllParticipants.length;
      console.log({ n3 });
      this.Qty = n3;
    },
    addNewNonMember(member) {
      console.log("addNewNonMember called");
      this.addNonMemberRecordPrompt = false;
      console.log({ member });
      this.NonMemberDetails = member;
      this.addNewRow(1);
    },
    addNewRow(tableId) {
      console.log("addNewRow called");

      console.log("tableId=" + tableId);

      switch (tableId) {
        case 1:
          var n2 = this.AllParticipants.length;
          console.log({ n2 });
          var MemberFlag = this.NonMemberDetails.MemberFlag;
          MemberFlag = MemberFlag == (null || undefined) ? false : MemberFlag;
          console.log({ MemberFlag });
          var m2 = this.ParticipantMaxLimit;
          console.log({ m2 });
          if (n2 < m2) {
            if (MemberFlag) {
              this.AllParticipants.push(this.NonMemberDetails);
              this.setQty();
            }
          } else {
            var message = "Participant maximum limit " + m2 + " reached. ";
            this.sweetAlert("error", message, false);
          }
          this.NonMemberDetails = {};
          break;

        default:
          break;
      }
    },
    deleteRow(tableId, index, tr) {
      console.log("deleteRow called");
      console.log({ tableId });
      console.log({ index });
      console.log({ tr });
      if (index >= 0) {
        switch (tableId) {
          case 1:
            this.AllParticipants.splice(index, 1);
            this.setQty();
            break;

          default:
            break;
        }
      }
    },
    getMemberIdOptions() {
      console.log("getMemberIdOptions called");
      var RoleId = this.RoleId;
      console.log({ RoleId });
      var UserInterface = RoleId == 9 ? 4 : 3;
      console.log({ UserInterface });
      var selectbox1_source = "MemberId";
      var selectbox1_destination = "MemberIdOptions";
      var selectbox1_url = "api/members/options";
      var selectbox1_conditions_array = {
        UserInterface: UserInterface,
        LomCode: this.LomCode,
        ZoneCode: this.ZoneCode,
      };
      this.getSelectBoxOptions(
        selectbox1_source,
        selectbox1_destination,
        selectbox1_conditions_array,
        selectbox1_url
      );
    },
    getSelectedMemberDetails() {
      console.log("getSelectedMemberDetails called");
      this.selectedMember = {};
      this.AllParticipants = [];
      var temp_code = this.MemberId;
      console.log("temp_code=" + temp_code);
      var idx = this.MemberIdOptions.map((e) => e.value).indexOf(temp_code);
      console.log("idx=" + idx);
      if (idx > 0) {
        var member = this.MemberIdOptions[idx];
        console.log("member=" + JSON.stringify(member));
        var tempData = {
          MemberFlag: true,
          MemberId: member.value,
          MembershipId: member.membership_id,
          MemberName: member.member_name,
          Gender: member.gender,
          FoodChoice: member.food_choice,
          Age: "",
          MobileNo: "",
          EmailId: "",
          MemberImagePath: member.photo,
          action: "",
        };
        // this.AllParticipants.push(tempData);
        this.selectedMember = tempData;
        this.Qty = 1;
      } else {
        this.Qty = 0;
      }
    },
    getMemberDetails() {
      console.log("getMemberDetails is called");

      var MemberId = this.UserMemberId;
      var ZoneCode = this.ZoneCode;
      var LomCode = this.LomCode;
      console.log(
        "MemberId=" +
          MemberId +
          "; ZoneCode=" +
          ZoneCode +
          "; LomCode=" +
          LomCode
      );

      if (MemberId != "" && ZoneCode != "" && LomCode != "") {
        this.LoadingFlag = true;
        this.Member = {};

        var server_url = companyConfig.apiURL;
        var token = this.$session.get("token");
        token = token == (null || undefined) ? 0 : token;
        var add_url = server_url + "api/members/search";
        var upload = {
          UserInterface: 4,
          MemberId: MemberId,
          ZoneCode: ZoneCode,
          LomCode: LomCode,
        };
        console.log("upload=" + JSON.stringify(upload));
        console.log(
          "server_url=" +
            server_url +
            ", token=" +
            token +
            ", add_url=" +
            add_url
        );

        const thisIns = this;
        var output = "";
        var records = "";
        var flag = 0;

        this.$http({
          url: add_url,
          method: "POST",
          data: upload,
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + token,
          },
          onUploadProgress: function (progressEvent) {
            this.uploadPercentage = parseInt(
              Math.round((progressEvent.loaded / progressEvent.total) * 100)
            );
          }.bind(this),
        })
          .then(function (response) {
            console.log("response=" + JSON.stringify(response));

            // thisIns.pageLoadingOff()
            thisIns.LoadingFlag = false;

            output = response.data.output;
            flag = response.data.flag;
            console.log("output=" + output + ", flag=" + flag);

            records = response.data.records;
            console.log({ records });

            if (flag == 1) {
              thisIns.Member = records;
            } else {
              thisIns.toast("error", output);
            }
          })
          .catch(function (error) {
            console.log("error=" + error);
            thisIns.LoadingFlag = false;
          });
      } else {
        var message = "";
        if (MemberId == "" || ZoneCode == "" || LomCode == "") {
          message += "Your session is expired. Kindly login again. ";
        }
        this.toast("error", message);
      }
    },
    getEventsDetails() {
      console.log("getEventsDetails is called");

      var EventCode = this.EventId;
      console.log("EventCode=" + EventCode);

      if (EventCode != "") {
        this.SubmitFlag = true;
        this.StartupLoadingFlag = true;
        this.Events = {};
        this.Plans = [];

        var server_url = companyConfig.apiURL;
        var token = this.$session.get("token");
        token = token == (null || undefined) ? 0 : token;
        var add_url = server_url + "api/jci-program/plan/lists";
        var upload = {
          UserInterface: 2,
          Program: EventCode,
          ProgramType: this.EventTypeId,
        };
        console.log("upload=" + JSON.stringify(upload));
        console.log(
          "server_url=" +
            server_url +
            ", token=" +
            token +
            ", add_url=" +
            add_url
        );

        const thisIns = this;
        var output = "";
        var records = "";
        var flag = 0;

        this.$http({
          url: add_url,
          method: "POST",
          data: upload,
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + token,
          },
          onUploadProgress: function (progressEvent) {
            this.uploadPercentage = parseInt(
              Math.round((progressEvent.loaded / progressEvent.total) * 100)
            );
          }.bind(this),
        })
          .then(function (response) {
            console.log("response=" + JSON.stringify(response));

            // thisIns.pageLoadingOff()
            thisIns.SubmitFlag = false;
            thisIns.StartupLoadingFlag = false;

            output = response.data.output;
            flag = response.data.flag;
            console.log("output=" + output + ", flag=" + flag);

            records = response.data.records;
            console.log({ records });

            if (flag == 1) {
              thisIns.Plans = records.TableData;
              thisIns.setQty();
              // thisIns.toast("success", output);
              // thisIns.resetForm();
            } else {
              thisIns.toast("error", output);
            }
          })
          .catch(function (error) {
            console.log("error=" + error);
            thisIns.SubmitFlag = false;
            thisIns.StartupLoadingFlag = false;
          });
      } else {
        var message = "";
        if (EventCode == "") {
          message += "Event Id should not be empty ";
        }
        this.toast("error", message);
      }
    },
    confirmAlert(selectedPlan) {
      console.log("confirmAlert called");

      var validate1 = this.$refs.form2.validate();
      console.log({ validate1 });

      var selectedMember = this.selectedMember;
      console.log({ selectedMember });

      var MemberType = this.MemberType;
      console.log({ MemberType });

      var RegistrationTypeId = MemberType <= 2 ? 3 : 4;
      console.log({ RegistrationTypeId });

      var MemberId = selectedMember.MemberId;
      MemberId = MemberId == (null || undefined) ? "" : MemberId;
      console.log({ MemberId });

      this.selectedPlan = selectedPlan;
      console.log({ selectedPlan });

      var EventPlanId = selectedPlan.EventPlanId;
      EventPlanId = EventPlanId == (null || undefined) ? "" : EventPlanId;
      console.log({ EventPlanId });

      var participants = this.AllParticipants;
      console.log({ participants });

      var TotalCount = this.Qty;
      console.log("TotalCount=" + TotalCount);

      var EventTotalAmount = selectedPlan.SubTotalAmount;
      console.log("EventTotalAmount=" + EventTotalAmount);

      var TotalAmount = parseFloat(TotalCount) * parseFloat(EventTotalAmount);
      console.log("TotalAmount=" + TotalAmount);

      if (validate1 && TotalCount > 0 && TotalAmount > 0 && MemberType > 0) {
        this.RegistrationTypeId = RegistrationTypeId;

        var MemberTxt = "";
        participants.forEach((item) => {
          console.log({ item });
          MemberTxt +=
            "<br/>" +
            item.ParticipantName +
            " &nbsp; - " +
            item.MobileNo +
            " &nbsp; - " +
            item.EmailId +
            " &nbsp; - " +
            item.Food;
        });
        var htmlTxt = "";
        htmlTxt += "<br/><b>Selected plan details</b>";
        htmlTxt += "<br/>Plan name <b>" + selectedPlan.PlanName + "</b>";
        htmlTxt += "<br/><br/><b>Selected participant details</b>";

        if (RegistrationTypeId == 3) {
          htmlTxt +=
            "<br/>Member Name <b>" + selectedMember.MemberName + "</b>";
          htmlTxt += "<br/>Mobile no. <b>" + selectedMember.MobileNo + "</b>";
          htmlTxt += "<br/>Email id <b>" + selectedMember.EmailId + "</b>";
          htmlTxt +=
            "<br/><img width='100' height='100' src='" +
            selectedMember.MemberImagePath +
            "' />";
        } else {
          htmlTxt +=
            "<br/><b> Member Name &nbsp; - Mobile no. &nbsp; - Email id &nbsp; - Food </b>";
          htmlTxt += MemberTxt;
        }

        htmlTxt += "<br/><br/>Total amount <b> ₹ " + TotalAmount + "</b>";
        htmlTxt += "<br/><br/>Do you want to continue?";
        Swal.fire({
          title: "Please verify all the information before proceeding",
          text: "",
          icon: "warning",
          html: htmlTxt,
          showCancelButton: true,
          confirmButtonText: `Continue`,
          confirmButtonColor: "#3085d6",
          cancelButtonText: `Cancel`,
          cancelButtonColor: "#d33",
        }).then((result) => {
          /* Read more about isConfirmed, isDenied below */
          if (result.isConfirmed) {
            this.submitForm();
            // this.makePayment();
          } else {
            this.sweetAlert("error", "You cancelled the process", true);
          }
        });
      } else {
        var message = "";
        if (!validate1) {
          message += "Kindly fill the required fields. ";
        }
        if (MemberId == "") {
          message += "Kindly select member. ";
        }
        if (EventPlanId == "") {
          message += "Kindly select plan. ";
        }
        if (TotalCount == 0) {
          message += "Kindly add atleast one participant. ";
        }
        this.sweetAlert("error", message, false);
      }
    },
    submitForm() {
      console.log("submitForm is called");

      var validate1 = this.$refs.form2.validate();
      console.log({ validate1 });

      var selectedPlan = this.selectedPlan;
      console.log({ selectedPlan });

      var selectedMember = this.selectedMember;
      console.log({ selectedMember });

      var MemberType = this.MemberType;
      console.log({ MemberType });

      var RegistrationTypeId = this.RegistrationTypeId;
      console.log({ RegistrationTypeId });

      var MemberId = selectedMember.MemberId;
      MemberId = MemberId == (null || undefined) ? "" : MemberId;
      console.log({ MemberId });

      var EventPlanId = selectedPlan.EventPlanId;
      EventPlanId = EventPlanId == (null || undefined) ? "" : EventPlanId;
      console.log({ EventPlanId });

      var participants = this.AllParticipants;
      console.log({ participants });

      var TotalCount = this.Qty;
      console.log("TotalCount=" + TotalCount);

      var EventTotalAmount = selectedPlan.TotalAmount;
      console.log("EventTotalAmount=" + EventTotalAmount);

      var TotalAmount = parseFloat(TotalCount) * parseFloat(EventTotalAmount);
      console.log("TotalAmount=" + TotalAmount);

      if (
        validate1 &&
        TotalCount > 0 &&
        TotalAmount > 0 &&
        RegistrationTypeId > 0
      ) {
        var server_url = companyConfig.apiURL;
        var token = this.$session.get("token");
        token = token == (null || undefined) ? 0 : token;
        var add_url = server_url + "api/jci-program/order/create";
        var upload = {
          UserInterface: RegistrationTypeId,
          RegistrationType: RegistrationTypeId,
          Lom: this.LomCode,
          Member: MemberId,
          Event: this.EventId,
          Plan: EventPlanId,
          MobileNo: selectedMember.MobileNo,
          EmailId: selectedMember.EmailId,
          Gender: MemberType == 3 ? null : selectedMember.Gender,
          FoodPreference: MemberType == 3 ? null : selectedMember.FoodChoice,
          GstNumberFlag: selectedMember.GstNumberFlag,
          GstNumber:
            selectedMember.GstNumberFlag == 1 ? selectedMember.GstNumber : null,
          TotalCount: TotalCount,
          TotalAmount: TotalAmount,
          Participants: participants,
        };
        console.log("upload=" + JSON.stringify(upload));
        console.log(
          "server_url=" +
            server_url +
            ", token=" +
            token +
            ", add_url=" +
            add_url
        );

        this.SubmitFlag = true;
        this.ProgressLoadingFlag = true;

        // this.$session.set("checkout", upload);
        // this.checkout();

        const thisIns = this;
        var output = "";
        var records = "";
        var successTxt = "";
        var errorTxt = "";
        var flag = 0;

        this.$http({
          url: add_url,
          method: "POST",
          data: upload,
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + token,
          },
          onUploadProgress: function (progressEvent) {
            this.uploadPercentage = parseInt(
              Math.round((progressEvent.loaded / progressEvent.total) * 100)
            );
          }.bind(this),
        })
          .then(function (response) {
            console.log("response=" + JSON.stringify(response));

            // thisIns.pageLoadingOff()
            thisIns.SubmitFlag = false;
            thisIns.ProgressLoadingFlag = false;

            output = response.data.output;
            flag = response.data.flag;
            output = response.data.output;
            records = response.data.records;
            console.log("output=" + output + ", flag=" + flag);
            successTxt = response.data.success;
            errorTxt = response.data.error;
            console.log("successTxt=" + successTxt + ", errorTxt=" + errorTxt);

            if (flag == 1) {
              /*
              thisIns.sweetAlert(
                "info",
                "You will be redirected to checkout page shortly",
                true
              );
              */
              thisIns.Order = records.Order;
              thisIns.makePayment();
            } else {
              // thisIns.toast("error", output);
              // thisIns.Order = records.Order;
              // thisIns.makePayment();
              thisIns.sweetAlert("info", output, false);
            }
          })
          .catch(function (error) {
            console.log("error=" + error);
            thisIns.SubmitFlag = false;
            thisIns.ProgressLoadingFlag = false;
          });
      } else {
        var message = "";
        if (!validate1) {
          message += "Kindly fill the required fields. ";
        }
        if (TotalCount == 0) {
          message += "Kindly add atleast one participant. ";
        }
        if (TotalAmount == 0) {
          message += "Kindly select a plan. ";
        }
        this.sweetAlert("error", message, false);
      }
    },
    makePayment() {
      console.log("makePayment");
      // ev.preventDefault();
      var order = this.Order;
      console.log({ order });
      const thisIns = this;
      var rzp1 = new Razorpay({
        key: order.rzp_key,
        account_id: order.rzp_account_id,
        order_id: order.rzp_order_id,
        amount: order.rzp_amount,
        name: order.rzp_name,
        currency: "INR",
        description: order.rzp_description,
        image: order.rzp_image,
        theme: {
          color: order.rzp_color,
        },
        prefill: {
          name: order.customer_name,
          email: order.customer_email_id,
          contact: order.customer_mobile_no,
        },
        notes: {
          address: "",
        },
        // callback_url: "http://domain.com/#/about/" + this.order_id,
        redirect: false,
        handler: function (response) {
          console.log("razorpay_payment_id=" + response.razorpay_payment_id);
          thisIns.SubmitFlag = true;
          // thisIns.$store.dispatch(ADD_BODY_CLASSNAME, "page-loading");
          thisIns.pageRedirect({}, "/order/success");
        },
      });
      rzp1.open();
    },
  },
  beforeMount() {
    this.refreshPageData();
    /*
    this.tableColumns1.push(
      { text: "Description", value: "Description" },
      { text: "Subscription Fees", value: "AmountTxt" },
      { text: "Nos", value: "Qty" },
      { text: "Sub Total", value: "SubTotalAmount" }
      // { text: "Actions", value: "actions" }
    );
    */
  },
};
</script>
<style lang="scss" scoped>
// Color Palette
$primary-color: #667eea;
$secondary-color: #764ba2;
$gradient-main: linear-gradient(
  135deg,
  $primary-color 0%,
  $secondary-color 100%
);
$success-color: #10b981;
$warning-color: #f59e0b;
$danger-color: #f44336;
$info-color: #2196f3;
$text-primary: #1a202c;
$text-secondary: #555;
$border-color: #e2e8f0;
$bg-light: #f7f9fc;

// Animation Keyframes
@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes float {
  0%,
  100% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-10px);
  }
}

@keyframes rotate {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

#sma-natcon-payment-create {
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  min-height: 100vh;
  padding: 30px 15px;

  // ============================================
  // HEADER CARD
  // ============================================
  .header-card {
    background: $gradient-main !important;
    border-radius: 16px;
    overflow: hidden;
    animation: slideDown 0.8s cubic-bezier(0.34, 1.56, 0.64, 1);
    position: relative;
    border: none;
    box-shadow: 0 20px 60px rgba(102, 126, 234, 0.4);

    .header-backdrop-effect {
      position: absolute;
      top: -50%;
      right: -50%;
      width: 200%;
      height: 200%;
      background: radial-gradient(
        circle,
        rgba(255, 255, 255, 0.1) 0%,
        transparent 70%
      );
      animation: rotate 20s linear infinite;
    }

    .header-content {
      position: relative;
      z-index: 1;
    }
  }

  .header-icon-box {
    background: rgba(255, 255, 255, 0.2);
    backdrop-filter: blur(10px);
    border-radius: 16px;
    padding: 12px;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
    animation: float 3s ease-in-out infinite;
  }

  .header-title {
    font-size: 32px;
    font-weight: 700;
    text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.2);
    animation: fadeInUp 0.8s ease-out;
    letter-spacing: 0.5px;
  }

  .header-subtitle {
    font-size: 16px;
    opacity: 0.95;
    animation: fadeInUp 1s ease-out;
    letter-spacing: 0.3px;
  }

  .action-btn {
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    font-weight: 700;
    letter-spacing: 0.5px;
    text-transform: uppercase;
    background: rgba(255, 255, 255, 0.15) !important;
    border: 2px solid rgba(255, 255, 255, 0.3) !important;
    backdrop-filter: blur(10px);
    color: white !important;

    &:hover {
      transform: translateY(-4px);
      box-shadow: 0 12px 24px rgba(0, 0, 0, 0.3) !important;
      background: rgba(255, 255, 255, 0.25) !important;
    }
  }

  // ============================================
  // NOTES ALERT
  // ============================================
  .notes-alert {
    border-radius: 12px !important;
    background: linear-gradient(
      135deg,
      rgba(33, 150, 243, 0.12) 0%,
      rgba(119, 235, 255, 0.08) 100%
    ) !important;
    border-left: 5px solid $info-color !important;
    animation: slideDown 0.8s cubic-bezier(0.34, 1.56, 0.64, 1);
    box-shadow: 0 8px 24px rgba(33, 150, 243, 0.15) !important;
    padding: 16px 20px !important;

    ::v-deep {
      .v-alert__icon {
        color: $info-color !important;
        align-self: flex-start !important;
        margin-top: 0px !important;
      }
    }
  }

  .alert-content {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .alert-title {
    font-size: 14px;
    font-weight: 700;
    color: $info-color;
    text-transform: uppercase;
    letter-spacing: 0.6px;
    margin: 0;
  }

  .alert-list {
    margin: 12px 0 0 20px;
    font-size: 13px;
    color: $text-secondary;
    line-height: 1.8;

    li {
      margin-bottom: 8px;
      font-weight: 500;
    }
  }

  // ============================================
  // ACTION BUTTONS
  // ============================================
  .action-buttons-row {
    display: flex;
    gap: 12px;
    justify-content: center;
    flex-wrap: wrap;

    .action-btn {
      transition: all 0.3s ease;
      font-weight: 700;
      letter-spacing: 0.5px;
      text-transform: uppercase;
      border-radius: 10px;
      padding: 12px 28px !important;
      box-shadow: 0 6px 20px rgba(0, 0, 0, 0.15);

      &.primary-btn {
        background: $gradient-main !important;
        color: white !important;

        &:hover {
          transform: translateY(-4px);
          box-shadow: 0 12px 32px rgba(102, 126, 234, 0.4) !important;
        }
      }

      &.warning-btn {
        background: linear-gradient(
          135deg,
          $warning-color 0%,
          #d97706 100%
        ) !important;
        color: white !important;

        &:hover {
          transform: translateY(-4px);
          box-shadow: 0 12px 32px rgba(245, 158, 11, 0.4) !important;
        }
      }

      .v-icon {
        margin-right: 8px;
      }
    }
  }

  // ============================================
  // FORM CARDS
  // ============================================
  .form-card {
    border-radius: 14px !important;
    border: none;
    box-shadow: 0 8px 32px rgba(102, 126, 234, 0.15);
    transition: all 0.3s ease;
    animation: fadeInUp 0.6s ease-out;

    &:hover {
      box-shadow: 0 16px 48px rgba(102, 126, 234, 0.25);
    }

    .form-card-header {
      display: flex;
      align-items: center;
      padding: 24px;
      background: linear-gradient(
        135deg,
        rgba(102, 126, 234, 0.08) 0%,
        rgba(118, 75, 162, 0.08) 100%
      );
      border-bottom: 3px solid $primary-color;

      .header-icon {
        color: $primary-color;
        flex-shrink: 0;
      }

      .ml-4 {
        margin-left: 16px;
        flex-grow: 1;
      }
    }

    .form-card-title {
      font-size: 20px;
      font-weight: 800;
      color: $text-primary;
      margin: 0;
      letter-spacing: 0.3px;
    }

    .form-card-subtitle {
      font-size: 13px;
      color: $text-secondary;
      margin: 4px 0 0 0;
      opacity: 0.8;
    }
  }

  // ============================================
  // REQUIRED NOTE CARD
  // ============================================
  .required-note-card {
    background: linear-gradient(135deg, rgba(102, 126, 234, 0.95) 0%, rgba(118, 75, 162, 0.95) 100%) !important;
    border-radius: 12px;
    padding: 16px 20px;
    border: 2px solid $primary-color;
    box-shadow: 0 6px 24px rgba(102, 126, 234, 0.2);
    animation: slideDown 0.6s cubic-bezier(0.34, 1.56, 0.64, 1);
    backdrop-filter: blur(10px);
    border: none !important;
  }

  .required-note-content {
    display: flex;
    align-items: center;
    gap: 16px;
    justify-content: center;
  }

  .required-note-icon {
    background: rgba(255, 255, 255, 0.2);
    border-radius: 50%;
    padding: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }

  .required-note-text {
    flex: 1;
  }

  .required-note-label {
    font-size: 14px;
    font-weight: 700;
    color: white;
    text-transform: uppercase;
    letter-spacing: 0.6px;
    display: flex;
    align-items: center;
    gap: 8px;

    .asterisk {
      font-size: 18px;
      color: #ffeb3b;
      font-weight: 900;
      text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
    }
  }

  .field-label {
    font-size: 13px;
    font-weight: 700;
    color: $text-primary;
    text-transform: uppercase;
    letter-spacing: 0.4px;
    display: flex;
    align-items: center;
    margin-bottom: 12px;

    .v-icon {
      flex-shrink: 0;
    }
  }

  .form-control {
    ::v-deep {
      .v-input__slot {
        border-radius: 8px !important;
      }

      .v-text-field--outlined {
        fieldset {
          border-color: $border-color;
          transition: all 0.3s ease;
        }
      }

      .v-text-field:hover {
        fieldset {
          border-color: $primary-color;
        }
      }

      .v-select--is-menu-active {
        .v-input__slot fieldset {
          border-color: $primary-color !important;
        }
      }
    }
  }

  // ============================================
  // MEMBER PHOTO & INFO
  // ============================================
  .member-photo-container {
    padding: 16px;
    background: $bg-light;
    border-radius: 12px;
    border: 2px solid $border-color;
    transition: all 0.3s ease;

    &:hover {
      border-color: $primary-color;
      box-shadow: 0 8px 20px rgba(102, 126, 234, 0.2);
    }

    .member-photo {
      border-radius: 12px;
      overflow: hidden;
    }
  }

  .placeholder-photo {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 140px;
    background: linear-gradient(
      135deg,
      rgba(102, 126, 234, 0.1) 0%,
      rgba(118, 75, 162, 0.1) 100%
    );
    border-radius: 12px;
  }

  .info-card {
    padding: 16px;
    background: linear-gradient(135deg, #f0f4ff 0%, #f8f5ff 100%);
    border-radius: 12px;
    border-left: 4px solid $primary-color;

    .info-value {
      font-size: 16px;
      font-weight: 700;
      color: $text-primary;
      margin: 0 0 4px 0;
    }

    .info-label {
      font-size: 11px;
      font-weight: 600;
      color: $text-secondary;
      text-transform: uppercase;
      letter-spacing: 0.4px;
      margin: 0 0 12px 0;
      opacity: 0.8;
    }
  }

  // ============================================
  // PROFESSIONAL TABLE
  // ============================================
  .professional-table {
    ::v-deep {
      .v-data-table {
        background: transparent !important;

        thead {
          th {
            background: linear-gradient(
              135deg,
              $primary-color 0%,
              $secondary-color 100%
            ) !important;
            color: white !important;
            font-weight: 700 !important;
            font-size: 12px !important;
            text-transform: uppercase !important;
            letter-spacing: 0.5px !important;
            border: none !important;
            padding: 16px 12px !important;

            &:first-child {
              border-radius: 12px 0 0 0;
            }

            &:last-child {
              border-radius: 0 12px 0 0;
            }
          }
        }

        tbody {
          tr {
            &:hover {
              background: rgba(102, 126, 234, 0.08) !important;
              box-shadow: inset 0 2px 8px rgba(102, 126, 234, 0.1);
            }

            &:not(:last-child) td {
              border-bottom: 1px solid $border-color !important;
            }

            td {
              padding: 16px 12px !important;
              font-size: 13px !important;
              color: $text-secondary !important;
            }
          }
        }
      }

      .v-chip {
        font-weight: 600 !important;
        font-size: 12px !important;
        letter-spacing: 0.3px !important;
      }
    }
  }

  .table-row {
    transition: all 0.3s ease;

    &:hover {
      background: rgba(102, 126, 234, 0.05);
    }
  }

  .table-cell {
    padding: 12px 16px;
    vertical-align: middle;
  }

  // ============================================
  // QUANTITY DISPLAY
  // ============================================
  .quantity-display {
    font-size: 56px;
    font-weight: 900;
    background: $gradient-main;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    margin: 0;
    animation: fadeInUp 0.6s ease-out;
  }

  // ============================================
  // PLANS SECTION
  // ============================================
  .plans-section-title {
    font-size: 24px;
    font-weight: 800;
    color: $text-primary;
    display: flex;
    align-items: center;
    margin: 40px 0 24px 0;
    animation: fadeInUp 0.6s ease-out;
    letter-spacing: 0.3px;
  }

  .plans-container {
    gap: 24px;
  }

  // ============================================
  // PLAN CARDS (Keep existing styling)
  // ============================================
  $color: #8950fc;
  $color1: #191970;

  .cards {
    background: #fff;
    border-radius: 14px;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
    flex: 1;
    margin: 8px;
    padding: 20px;
    padding-top: 40px;
    height: 420px;
    width: 240px;
    position: relative;
    text-align: center;
    transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
    border: 2px solid #f0f0f0;
    animation: fadeInUp 0.6s ease-out;

    &:hover {
      transform: translateY(-12px);
      box-shadow: 0 20px 48px rgba(102, 126, 234, 0.3);
      border-color: $primary-color;
    }
  }

  .cards.popular {
    margin-top: -16px;
    height: 440px;
    padding: 24px;
    padding-top: 50px;
    width: 240px;
    box-shadow: 0 12px 40px rgba(102, 126, 234, 0.4);
    background: linear-gradient(135deg, #fff 0%, #f8f5ff 100%);

    &:hover {
      transform: translateY(-16px);
      box-shadow: 0 24px 56px rgba(102, 126, 234, 0.5);
    }
  }

  .cards-ribbon {
    position: absolute;
    overflow: hidden;
    top: -10px;
    left: -10px;
    width: 114px;
    height: 112px;
  }

  .cards-ribbon span {
    position: absolute;
    z-index: 1;
    display: block;
    width: 160px;
    padding: 10px 0;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    box-shadow: 0 5px 15px rgba(102, 126, 234, 0.4);
    color: #fff;
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    text-align: center;
    left: -35px;
    top: 25px;
    transform: rotate(-45deg);
    letter-spacing: 0.5px;
  }

  .cards-title {
    color: $color1;
    font-size: 18px;
    font-weight: 800;
    text-transform: uppercase;
    margin: 0;
    letter-spacing: 0.4px;
  }

  .cards.popular .cards-title {
    color: $color;
    font-size: 20px;
  }

  .cards-subtitle {
    color: rgba(0, 0, 0, 0.6);
    font-size: 13px;
    margin: 8px 0 0 0;
    font-weight: 500;
  }

  .cards-price {
    margin: 24px 0 -20px 0;
  }

  .cards-price-amount {
    color: $color1;
    font-size: 32px;
    font-weight: 800;
    margin: 0;
  }

  .cards.popular .cards-price-amount {
    color: $color;
    font-size: 36px;
  }

  .cards-price-gst {
    color: rgba(0, 0, 0, 0.5);
    display: block;
    font-size: 12px;
    text-transform: uppercase;
    font-weight: 600;
    letter-spacing: 0.3px;
  }

  .cards-price-validity {
    color: rgba(0, 0, 0, 0.4);
    display: block;
    font-size: 11px;
    text-transform: uppercase;
    font-weight: 600;
    letter-spacing: 0.3px;
  }

  .cards-action {
    margin-top: 16px;
  }

  .cards-action-button {
    background: transparent;
    border: 2px solid $color;
    border-radius: 8px;
    color: $color;
    cursor: pointer;
    display: block;
    font-size: 14px;
    font-weight: 700;
    padding: 12px 20px;
    width: 100%;
    height: 50px;
    text-transform: uppercase;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    letter-spacing: 0.5px;

    &:hover:not(:disabled) {
      background-color: $color;
      color: #fff;
      box-shadow: 0 8px 20px rgba(137, 80, 252, 0.3);
      transform: translateY(-3px);
    }

    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }
  }

  .cards.popular .cards-action-button {
    background-color: $color;
    color: #fff;
    border-color: $color;

    &:hover:not(:disabled) {
      box-shadow: 0 8px 24px rgba(137, 80, 252, 0.5);
    }
  }

  // ============================================
  // RESPONSIVE DESIGN
  // ============================================
  @media (max-width: 1200px) {
    .header-title {
      font-size: 26px;
    }

    .header-subtitle {
      font-size: 14px;
    }

    .plans-section-title {
      font-size: 20px;
    }
  }

  @media (max-width: 960px) {
    padding: 20px 10px;

    .header-card {
      margin-bottom: 16px;
    }

    .header-title {
      font-size: 22px;
    }

    .action-btn {
      font-size: 12px !important;
      padding: 8px 12px !important;

      .v-icon {
        font-size: 16px !important;
      }
    }

    .form-card {
      .form-card-header {
        padding: 16px;
      }

      .form-card-title {
        font-size: 16px;
      }
    }

    .cards {
      width: 100%;
      height: auto;
      padding: 16px;
      margin: 8px 0;
    }

    .cards.popular {
      width: 100%;
    }
  }

  @media (max-width: 600px) {
    padding: 15px 8px;

    .header-title {
      font-size: 18px;
    }

    .header-subtitle {
      font-size: 11px;
    }

    .header-icon-box {
      width: 40px;
      height: 40px;
      padding: 8px;

      ::v-deep .v-icon {
        font-size: 24px !important;
      }
    }

    .form-card {
      margin-bottom: 16px;

      .form-card-header {
        flex-direction: column;
        align-items: flex-start;
        gap: 12px;
      }

      .form-card-title {
        font-size: 14px;
      }

      .form-card-subtitle {
        font-size: 12px;
      }
    }

    .quantity-display {
      font-size: 36px;
    }

    .cards {
      width: 100%;
      padding: 12px;
      height: auto;
    }

    .professional-table {
      ::v-deep {
        .v-data-table {
          thead th {
            padding: 12px 8px !important;
            font-size: 10px !important;
          }

          tbody tr td {
            padding: 12px 8px !important;
            font-size: 11px !important;
          }
        }

        .v-chip {
          font-size: 10px !important;
        }
      }
    }
  }

  // ============================================
  // UTILITY CLASSES
  // ============================================
  .d-flex {
    display: flex;
  }

  .justify-content-center {
    justify-content: center;
  }

  .justify-end {
    justify-content: flex-end;
  }

  .align-center {
    align-items: center;
  }

  .text-right {
    text-align: right;
  }

  .text-center {
    text-align: center;
  }

  .text-danger {
    color: $danger-color;
    font-weight: 700;
  }

  .white--text {
    color: white;
  }

  .flex-wrap {
    flex-wrap: wrap;
  }

  .overflow-hidden {
    overflow: hidden;
  }

  .ml-4 {
    margin-left: 16px;
  }

  .ml-6 {
    margin-left: 24px;
  }

  .mr-2 {
    margin-right: 8px;
  }

  .mr-3 {
    margin-right: 12px;
  }

  .mb-3 {
    margin-bottom: 12px;
  }

  .mb-6 {
    margin-bottom: 24px;
  }

  .my-3 {
    margin: 12px 0;
  }

  .my-0 {
    margin: 0;
  }

  .pa-8 {
    padding: 32px;
  }

  .pa-6 {
    padding: 24px;
  }

  .pt-6 {
    padding-top: 24px;
  }

  .gap-2 {
    gap: 8px;
  }

  .rounded-circle {
    border-radius: 50%;
  }
}
</style>
